import { ActionType } from 'typesafe-actions';
import { select, put, takeEvery } from 'redux-saga/effects';

import { getErpPlanLabel } from 'PlanListPackage/MainModule/common/mapPlanStatus';
import { getPlanErknmStatusTitleByStatus } from 'APIPackage/PlanListModule/common/getPlanErknmStatusTitleByStatus';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { ErknmPlanStatusesEnum } from 'APIPackage/PlanListModule/enums/ErknmPlanStatusesEnum';
import { PlanStatusEnum } from 'APIPackage/PlanListModule/enums/PlanStatusEnum';
import { IFilterResult } from 'APIPackage/MainModule/interfaces/IFilterResult';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  fetchPlanFilters,
  fetchPlanFilterById,
  createPlanFilter,
  deletePlanFilter,
  updatePlanFilter,
} from 'APIPackage/PlanListModule/services/knmPlanApi';
import { IKnmPlanFilterRaw } from 'APIPackage/PlanListModule/interfaces/IKnmPlanFilterRaw';
import {
  getUserKnoSelectOptions,
  getUserProsecutorSelectOptions,
} from 'CorePackage/CatalogsModule/selectors/catalogsSelectors';
import { getPlanTypeFilterSelectOptions } from 'CorePackage/DictionariesModule/selectors/dictionariesSelectors';
import { ISelectOption } from 'UIPackage/MainModule/components/SelectInput';
import { IUserOrganisationSelectOption } from 'CorePackage/CatalogsModule/interfaces/IUserOrganisationSelectOption';
import { getStorageItem } from 'CorePackage/MainModule/common/localStore';
import { StorageNamesEnum } from 'CorePackage/MainModule/enums/StorageNamesEnum';
import { IKnmPlanFilter } from 'PlanListPackage/PlanListFiltersModule/interfaces/IKnmPlanFilter';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';

import { getPlanListStatuses } from '../../MainModule/selectors/planListSelectors';
import { KnmPlanListFilterActionsEnum } from '../enums/KnmPlanListFilterActionsEnum';
import {
  planListFilterSetLoading,
  planListFilterSetMeta,
  planListFilterGetData,
  planListFilterSetData,
  planListFilterCreate,
  planListFilterRemove,
  planListFilterUpdate,
  planListFilterSetCurrent,
} from '../actions/planListFilterActions';
import { mapRawToPlanFilter } from '../common/mapRawToPlanFilter';
import { mapPlanFilterToRaw } from '../common/mapPlanFilterToRaw';

export function* watchPlanListFilterSaga() {
  yield takeEvery(KnmPlanListFilterActionsEnum.PLAN_LIST_FILTER_GET_META, fetchFiltersSaga);
  yield takeEvery(KnmPlanListFilterActionsEnum.PLAN_LIST_FILTER_GET_DATA, fetchFilterByIdSaga);
  yield takeEvery(KnmPlanListFilterActionsEnum.PLAN_LIST_FILTER_CREATE, createPlanFilterSaga);
  yield takeEvery(KnmPlanListFilterActionsEnum.PLAN_LIST_FILTER_REMOVE, removePlanListFilterSaga);
  yield takeEvery(KnmPlanListFilterActionsEnum.PLAN_LIST_FILTER_UPDATE, updatePlanListFilterSaga);
}

function* fetchFiltersSaga() {
  function* funcTry() {
    yield put(planListFilterSetLoading(true));
    const meta: IFilterResult[] = yield fetchPlanFilters();

    yield put(planListFilterSetMeta(meta));
    const current = getStorageItem<IKnmPlanFilter>(StorageNamesEnum.PLAN_FILTER);
    const currentId = meta.find(m => m.name === current?.name)?.id;

    yield put(planListFilterSetCurrent({ current, currentId }));
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(planListFilterSetLoading(false));
    },
  });
}

function* fetchFilterByIdSaga(action: ActionType<typeof planListFilterGetData>) {
  function* funcTry() {
    const { id } = action.payload;
    const state: IRootState = yield select();
    const { filters } = state.planListFilter;
    const knoOptions: IUserOrganisationSelectOption[] | undefined = getUserKnoSelectOptions(state) ?? [];
    const prosecutorOptions: IUserOrganisationSelectOption[] | undefined = getUserProsecutorSelectOptions(state) ?? [];
    const planTypeOptions: ISelectOption[] = getPlanTypeFilterSelectOptions(state) ?? [];

    const isFederalLaw248: boolean = getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const planStatuses: Array<ErknmPlanStatusesEnum | PlanStatusEnum> = getPlanListStatuses(state);
    const planStatusOptions: ISelectOption<string>[] = planStatuses.map(status => {
      const planLabel = isFederalLaw248
        ? getPlanErknmStatusTitleByStatus(status as ErknmPlanStatusesEnum)
        : getErpPlanLabel(status as PlanStatusEnum);

      return { id: status, label: planLabel || '', value: status };
    });

    if (filters.find(f => f.meta.id === id)?.data !== undefined) {
      return;
    }
    yield put(planListFilterSetLoading(true));
    const raw: IKnmPlanFilterRaw = yield fetchPlanFilterById(id);
    const filter = mapRawToPlanFilter(raw, prosecutorOptions, knoOptions, planTypeOptions, planStatusOptions);

    yield put(planListFilterSetData(id, filter));
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(planListFilterSetLoading(false));
    },
  });
}

function* createPlanFilterSaga(action: ActionType<typeof planListFilterCreate>) {
  function* funcTry() {
    const { data } = action.payload;

    yield put(planListFilterSetLoading(true));
    const state: IRootState = yield select();
    const rawFilter = mapPlanFilterToRaw(data);
    const meta: IFilterResult = yield createPlanFilter(rawFilter);
    const metaList = state.planListFilter.filters.map(f => f.meta);

    yield put(planListFilterSetMeta([...metaList, meta]));
    yield put(planListFilterSetData(meta.id, data));
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(planListFilterSetLoading(false));
    },
  });
}

function* removePlanListFilterSaga(action: ActionType<typeof planListFilterRemove>) {
  const { id } = action.payload;

  yield sagaWrapper({
    *funcTry() {
      yield put(planListFilterSetLoading(true));
      yield deletePlanFilter(id);
    },
    *funcFinally() {
      yield put(planListFilterSetLoading(false));
    },
  });
}

function* updatePlanListFilterSaga(action: ActionType<typeof planListFilterUpdate>) {
  const { id, data } = action.payload;

  function* funcTry() {
    yield put(planListFilterSetLoading(true));
    const rawFilter = mapPlanFilterToRaw(data);

    yield updatePlanFilter(id, rawFilter);
    yield put(planListFilterSetData(id, data));
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(planListFilterSetLoading(false));
    },
  });
}
